<template>
    <card title="PV1 Queue">
        <b-row align-v="center">
            <b-col cols="12" lg="5">
                <SearchComponent v-model="storeFilter" label="Store" search-u-r-l="/Store" :params="{active: true}" prepopulate clearable
                    :disabled="isLoading" @input="load" />
            </b-col>
            <b-col cols="12" lg="5">
                <SearchComponent v-model="programFilter" label="Program" search-u-r-l="/Program" prepopulate clearable
                    :disabled="isLoading" @input="load" />
            </b-col>
            <b-col cols="2">
                <b-form-checkbox v-model="includeOnHold" @input="load">
                    Include On Hold
                </b-form-checkbox>
                <b-form-checkbox v-model="includeLocked" @input="load">
                    Include Locked
                </b-form-checkbox>
            </b-col>
        </b-row>
        <b-row v-if="openErrorCount">
            <b-col>
                <Checkbox v-model="showOpenDispense">
                    Open {{dispenseErrorLabel + "s"}} ({{ openErrorCount }})
                </Checkbox>
            </b-col>
        </b-row>

        <PrescriptionsListComponent ref="prescriptionsList" default-sort="created" :prescriptions="pagedPrescriptions"
            :additionalFields="additionalFields" :action-buttons="actionButtons" :is-loading="isLoading"
            :show-void-box="false" current-sort="created" :current-desc="false"
            show-select-checkox show-patient-name :colorCodes="handleRowStyles"
            @selection-changed="selectionChanged" />
        <BottomPagerBar v-model="pageNumber" item-name="prescription" :total-rows="filteredPrescriptions.length"
            :per-page="pageSize" :loading="isLoading" show-total show-page-size-selector
            :selected-rows="selectedPrescriptions.length" @change-page-size="pageSize = $event"
            @clear-selection="clearSelection">
            <template #left-after>
                <b-button variant="success" size="sm" @click="processNext"
                    :disabled="isLoading || filteredPrescriptions.length === 0 || selectedPrescriptions.length > 0">
                    Process Next Available Script
                </b-button>
            </template>
            <template #fab>
                <b-button v-if="selectedPrescriptions.length > 0" variant="success" @click="openBatchVerification" :disabled="isLoading">
                    Process Selected
                </b-button>
            </template>
        </BottomPagerBar>
    </card>
</template>

<script lang="ts">
import { Component, Mixins } from 'vue-property-decorator';
import { DatePicker } from "element-ui";
import SearchComponent from '@/components/SearchComponent.vue';
import PrescriptionsListComponent from '@/components/Prescription/PrescriptionsListComponent.vue';
import { Prescription } from '@/models/Prescription';
import { Store } from '@/models/Store';
import Checkbox from "@/components/Inputs/Checkbox.vue";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { AxiosError } from 'axios';
import moment from "moment/moment";
import BottomPagerBar from "@/components/BottomPagerBar.vue";
import { Program } from "@/models/Program";
import AutoModeHotkeyMixin from '@/mixins/AutoModeHotkeyMixin';

@Component({
    name: "PV1Queue",
    components: {
        BottomPagerBar,
        SearchComponent,
        [DatePicker.name]: DatePicker,
        PrescriptionsListComponent,
        Checkbox,
    },
})
export default class PV1Queue extends Mixins(AutoModeHotkeyMixin) {
    protected prescriptions: Array<Prescription> = [];
    protected selectedPrescriptions: Array<Prescription> = [];
    private storeFilter: Store = new Store();
    private programFilter: Program = new Program();

    protected isLoading = false;
    protected actionButtons = ['pv1'];
    protected showOpenDispense = false;
    protected includeOnHold = false;
    protected includeLocked = false;

    protected pageNumber: number = 1;
    protected pageSize: number = 100;
    protected currentBatchIndex: number = 0;

    get additionalFields() {
        const fields = [
            {
                index: 9,
                field: {
                    key: "created",
                    label: "Created",
                    sortable: true,
                    sortDirection: "asc",
                    formatter: this.dateFormatter,
                },
            }
        ];

        if (this.includeLocked) {
            fields.push({
                index: 10,
                field: {
                    key: "locked",
                    label: "Locked By",
                    sortable: false,
                    class: "locked",
                },
            });
        }

        return fields;
    };

    dateFormatter(value: Date, _key: string, _item: any) {
        return moment.utc(value).format("L");
    }

    handleRowStyles(item: any, type: any) {
        if (!item || type !== 'row') return;

        if (item.lockedBy) {
            return "bg-warning locked";
        }

        return "";
    }

    mounted() {
        this.load();
        this.fetchDispenseErrorLabel();
    }

    selectionChanged(selectedPrescriptions: Array<Prescription>) {
        this.selectedPrescriptions = selectedPrescriptions;
    }

    async load() {
        this.isLoading = true;
        try {
            const config = {
                params: {
                    includeOnHold: this.includeOnHold,
                    includeLocked: this.includeLocked
                }
            };
            const response = await this.$http.get<Prescription[]>('api/prescription/pv1-queue', config);
            this.prescriptions = response.data;
        } catch (err) {
            const error = err as AxiosError;
            console.error("Error while loading PV1 queue.", { error, response: error?.response });
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.isLoading = false;
        }
    }

    get pagedPrescriptions() {
        const startIndex = this.pageSize * this.pageNumber - this.pageSize;
        const endIndex = startIndex + this.pageSize;
        return this.filteredPrescriptions.slice(startIndex, endIndex);
    }

    get filteredPrescriptions() {
        let scripts = this.prescriptions;
        if (this.storeFilter?.id) {
            scripts = scripts.filter(p => p.storeID == this.storeFilter.id);
        }
        if (!this.showOpenDispense) {
            scripts = scripts.filter(p => !p.dispenseErrorId);
        }
        if (this.programFilter?.id) {
            scripts = scripts.filter(p => p.programID == this.programFilter.id);
        }
        return scripts;
    }

    get openErrorCount() {
        return this.prescriptions?.filter(p => !this.storeFilter?.id || p.storeID == this.storeFilter.id)
            .filter(p => p.dispenseErrorId).length;
    }

    protected dispenseErrorLabel = "";

    async fetchDispenseErrorLabel() {
        try {
            const resp = await this.$http.get("/Configuration/DispenseErrorLabel");
            this.dispenseErrorLabel = resp.data?.trim();
        } catch {
            this.dispenseErrorLabel = "Error";
            //ignore
        }
    }

    clearSelection() {
        this.selectedPrescriptions = [];
        // Clear the child component's selection state
        if (this.$refs.prescriptionsList) {
            (this.$refs.prescriptionsList as any).prescriptionsSelected = [];
        }
    }

    processNext() {
        if (this.filteredPrescriptions.length === 0) {
            this.$notification(NotificationOptions.warning("No prescriptions available to process"));
            return;
        }

        // Get the first available prescription from the filtered list
        const nextPrescription = this.filteredPrescriptions[0];

        // Navigate to the prescription for processing
        this.$router.push({
            name: 'PV1',
            params: {
                rxId: nextPrescription.rxID?.toString() || '',
                storeID: nextPrescription.storeID?.toString() || '',
                rxNumber: nextPrescription.rxNumber?.toString() || '',
                rfNumber: nextPrescription.rfNumber?.toString() || ''
            },
            query: {
                batch: 'auto',
            }
        });
    }

    openBatchVerification() {
        if (this.selectedPrescriptions.length === 0) {
            this.$notification(NotificationOptions.warning("Please select at least one prescription"));
            return;
        }

        // Store selected prescriptions in the global store (similar to ingestion queue)
        const firstPrescription = this.selectedPrescriptions[0];
        const remainingPrescriptions = this.selectedPrescriptions.slice(1);

        // Store remaining prescriptions in the store for batch processing
        this.$store.pv1BatchPrescriptions = remainingPrescriptions;

        // Navigate to the first prescription in the batch
        this.$router.push({
            name: 'PV1',
            params: {
                rxId: firstPrescription.rxID?.toString() || '',
                storeID: firstPrescription.storeID?.toString() || '',
                rxNumber: firstPrescription.rxNumber?.toString() || '',
                rfNumber: firstPrescription.rfNumber?.toString() || ''
            },
            query: {
                batch: remainingPrescriptions.length > 0 ? 'true' : 'false',
            }
        });
    }

}

</script>

<style scoped>
::v-deep(.locked) {
    width: 200px;
}
</style>
